import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';

export type MealType = 'breakfast' | 'lunch' | 'dinner' | 'milk' | 'juice';

export interface MealDelivery {
  id: string;
  site_id: string;
  service_date: string;
  meal_type: MealType;
  quantity_received: number;
  menu_description: string | null;
  menu_items: string[];
  received_by: string | null;
  received_at: string | null;
  notes: string | null;
}

export const useMealDeliveries = (siteId?: string | null, dateRange?: { start: Date; end: Date }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const startStr = dateRange ? format(dateRange.start, 'yyyy-MM-dd') : undefined;
  const endStr = dateRange ? format(dateRange.end, 'yyyy-MM-dd') : undefined;

  const query = useQuery({
    queryKey: ['meal-deliveries', siteId ?? 'all', startStr, endStr],
    queryFn: async () => {
      let q = (supabase as any).from('meal_deliveries').select('*').order('service_date', { ascending: false });
      if (siteId) q = q.eq('site_id', siteId);
      if (startStr) q = q.gte('service_date', startStr);
      if (endStr) q = q.lte('service_date', endStr);
      const { data, error } = await q;
      if (error) throw error;
      return (data || []) as MealDelivery[];
    },
  });

  const upsert = useMutation({
    mutationFn: async (input: {
      site_id: string;
      service_date: string; // yyyy-MM-dd
      meal_type: MealType;
      quantity_received: number;
      menu_description?: string | null;
      menu_items?: string[];
      notes?: string | null;
    }) => {
      const payload = {
        ...input,
        menu_items: input.menu_items ?? [],
        received_by: user?.id ?? null,
        received_at: new Date().toISOString(),
      };
      const { data, error } = await (supabase as any)
        .from('meal_deliveries')
        .upsert(payload, { onConflict: 'site_id,service_date,meal_type' })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meal-deliveries'] });
      toast({ title: 'Saved', description: 'Meal delivery updated' });
    },
    onError: (e: any) =>
      toast({ variant: 'destructive', title: 'Error', description: e.message ?? 'Failed to save meal delivery' }),
  });

  return {
    deliveries: query.data ?? [],
    isLoading: query.isLoading,
    upsertDelivery: upsert.mutate,
  };
};
